import { Alert, Box, MenuItem, Stack } from '@mui/material';
import { useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { enqueueSnackbar } from 'notistack';
import axios, { endpoints } from 'src/utils/axios';
import { CAMERA_VIDEO_STATUS_QUEUED, Device } from 'src/models';
import { useAuthContext } from 'src/auth/hooks';
import SimpleDialog from 'src/components/simple-dialog';
import TruxFormField from 'src/components/inputs/trux-form-field';
import TruxSimpleSelectField from 'src/components/inputs/trux-simple-select-field';
import TruxSimpleDateTimeField from 'src/components/inputs/trux-simple-date-time-field';
import Iconify from 'src/components/iconify';

type RequestHistoricVideoDialogProps = {
  open: boolean;
  onClose: VoidFunction;
  device?: Device;
  devices?: (Device & { label?: string })[];
};

export default function RequestHistoricVideoDialog({
  open,
  onClose,
  device,
  devices,
}: RequestHistoricVideoDialogProps) {
  const now = DateTime.now();

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const [loading, setLoading] = useState(false);
  const [fromTime, setFromTime] = useState(now.minus({ seconds: 60 }).toISO()!);
  const [toTime, setToTime] = useState(now.toISO()!);
  const [camera, setCamera] = useState(0);
  const [selectedDeviceId, setSelectedDeviceId] = useState(
    device?.id || (devices?.length === 1 ? devices[0].id : 0)
  );

  const selectedDevice = device || devices?.find((line) => line.id === selectedDeviceId);

  // Ranges longer than 2 minutes can't be a single clip (the camera's clip mode
  // caps at 60s); the backend delivers them as a ~2-minute collapsed overview
  // sampled across the range. Warn the user so the timelapse result isn't a
  // surprise.
  const rangeSeconds = useMemo(() => {
    const from = DateTime.fromISO(fromTime);
    const to = DateTime.fromISO(toTime);
    if (!from.isValid || !to.isValid) return 0;
    return to.diff(from, 'seconds').seconds;
  }, [fromTime, toTime]);
  const isOverview = rangeSeconds > 120;

  return (
    <SimpleDialog
      open={open}
      onCancel={onClose}
      title={selectedDevice ? `Request Video for ${selectedDevice.deviceType}` : 'Request Historic Video'}
      onSubmit={() => {
        setLoading(true);

        axios
          .post(
            endpoints.cameras.requestHistoricVideo,
            JSON.stringify({ deviceId: selectedDevice!.id, fromTime, toTime, camera })
          )
          .then((response) => {
            const queued = response.data?.videoStatus === CAMERA_VIDEO_STATUS_QUEUED;
            let message =
              'Historic video has been requested. It will appear in the Safety Inbox once the camera uploads it.';
            if (queued) {
              message =
                'The camera is offline. Your request is queued and will be sent as soon as it reconnects.';
            } else if (isOverview) {
              message =
                'Building a 2-minute overview of the selected range. It will appear in the Safety Inbox in a few minutes.';
            }
            enqueueSnackbar(message, { autoHideDuration: 6000 });
            onClose();
          })
          .catch(() => {
            enqueueSnackbar('An unexpected error has occured.', { variant: 'error' });
          })
          .finally(() => {
            setLoading(false);
          });
      }}
      submitDisabled={loading || !selectedDevice}
      submitButton="Request Historic Video"
      submitButtonIcon={<Iconify icon="qlementine-icons:camera-16" />}
      submitButtonColor="info"
      sx={{ '.MuiDialogContent-root': { overflowY: 'visible' } }}
    >
      <Stack direction="column" gap={1}>
        <Alert
          icon={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Iconify icon="qlementine-icons:camera-16" />
            </Box>
          }
          severity="success"
          sx={{ mb: 2 }}
        >
          After you request a video you will be able to view it in the Safety Inbox.
        </Alert>

        {isOverview && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This range is longer than 2 minutes, so a condensed 2-minute overview will be created
            by sampling images across the time range. It may take a few minutes to appear.
          </Alert>
        )}

        {!device && (
          <TruxFormField label="Truck">
            <TruxSimpleSelectField value={selectedDeviceId} onChange={setSelectedDeviceId}>
              {!selectedDeviceId && (
                <MenuItem value={0} disabled>
                  Select a Truck
                </MenuItem>
              )}
              {(devices || []).map((line) => (
                <MenuItem key={line.id} value={line.id}>
                  {line.label || line.deviceType}
                </MenuItem>
              ))}
            </TruxSimpleSelectField>
          </TruxFormField>
        )}
        <TruxFormField label="Camera">
          <TruxSimpleSelectField value={camera} onChange={setCamera}>
            <MenuItem value={0}>Front Camera</MenuItem>
            <MenuItem value={1}>Interior Camera (Driver)</MenuItem>
          </TruxSimpleSelectField>
        </TruxFormField>
        <TruxFormField label="From Time">
          <TruxSimpleDateTimeField
            limitWidth={false}
            value={fromTime}
            onChange={setFromTime}
            timezone={timezone}
          />
        </TruxFormField>
        <TruxFormField label="To Time">
          <TruxSimpleDateTimeField
            limitWidth={false}
            value={toTime}
            onChange={setToTime}
            timezone={timezone}
          />
        </TruxFormField>
      </Stack>
    </SimpleDialog>
  );
}
